class PairsWithSelector extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.setupTemplate();
  }

  setupTemplate() {
    const template = document.createElement('template');
    template.innerHTML = `
      <style>
        :host {
          display: block;
        }
        
        .product-grid-item-pairs-with {
          cursor: pointer;
          position: relative;
          transition: opacity 0.2s ease-in-out;
        }
        
        .product-grid-item-pairs-with:hover {
          opacity: 0.9;
        }
        
        .product-grid-item-pairs-with.selected {
          outline: 2px solid #957861;
        }
        
        ::slotted([data-pairs-checkbox]) {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 2;
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #957861;
        }
        
        ::slotted(form) {
          margin: 0;
        }
      </style>
      <slot></slot>
    `;
    
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Initialize drag-to-scroll functionality
    const container = this.querySelector('.cart-drawer-pairs-products');
    if (container) {
      let isDown = false;
      let startY;
      let scrollTop;

      // Mouse Events
      container.addEventListener('mousedown', (e) => {
        isDown = true;
        container.style.cursor = 'grabbing';
        startY = e.pageY - container.offsetTop;
        scrollTop = container.scrollTop;
      });

      container.addEventListener('mouseleave', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mouseup', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const y = e.pageY - container.offsetTop;
        const walk = (y - startY) * 2;
        container.scrollTop = scrollTop - walk;
      });

      // Touch Events
      container.addEventListener('touchstart', (e) => {
        isDown = true;
        startY = e.touches[0].pageY - container.offsetTop;
        scrollTop = container.scrollTop;
      }, { passive: false });

      container.addEventListener('touchend', () => {
        isDown = false;
      });

      container.addEventListener('touchcancel', () => {
        isDown = false;
      });

      container.addEventListener('touchmove', (e) => {
        if (!isDown) return;
        const y = e.touches[0].pageY - container.offsetTop;
        const walk = (y - startY) * 2;
        container.scrollTop = scrollTop - walk;
      }, { passive: false });
    }

    // Initialize click handlers for products
    this.querySelectorAll('.product-grid-item-pairs-with').forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't handle clicks on the checkbox itself
        if (e.target.matches('[data-pairs-checkbox]')) return;
        this.toggleProductSelection(item);
      });
    });

    // Initialize form submit handler
    const form = this.querySelector('[data-pairs-form]');
    if (form) {
      form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }
  }

  toggleProductSelection(productCard) {
    const checkbox = productCard.querySelector('[data-pairs-checkbox]');
    if (!checkbox) return;

    checkbox.checked = !checkbox.checked;
    productCard.classList.toggle('selected', checkbox.checked);

    // Update confirm button state
    const submitButton = this.querySelector('[data-pairs-form] [data-add-to-cart]');
    if (submitButton) {
      submitButton.disabled = !this.querySelector('[data-pairs-checkbox]:checked');
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('product-selection-change', {
      detail: {
        productCard,
        checked: checkbox.checked
      },
      bubbles: true,
      composed: true
    }));
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const submitButton = form.querySelector('[data-add-to-cart]');
    const checkedBoxes = form.querySelectorAll('[data-pairs-checkbox]:checked');

    if (checkedBoxes.length === 0) return;

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding...';
    }

    try {
      const items = Array.from(checkedBoxes).map(checkbox => ({
        id: parseInt(checkbox.value),
        quantity: 1
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (response.ok) {
        // Dispatch success event
        this.dispatchEvent(new CustomEvent('pairs-added-to-cart', {
          detail: { items },
          bubbles: true,
          composed: true
        }));

        // Close the pairs drawer
        const pairsDrawer = document.querySelector('.cart-drawer-pairs');
        if (pairsDrawer) {
          pairsDrawer.classList.remove('active');
        }

        // Clear selections
        checkedBoxes.forEach(checkbox => {
          checkbox.checked = false;
          checkbox.closest('.product-grid-item-pairs-with').classList.remove('selected');
        });

        if (window.cart && window.cart.getCart) {
          window.cart.getCart();
          setTimeout(() => {
            this.manualCartUpdate();
          }, 500);
        } else {
          this.manualCartUpdate();
        }
      } else {
        const errorMessage = data.description || data.message || 'There was an error adding items to your cart. Please try again.';
        this.dispatchEvent(new CustomEvent('pairs-error', {
          detail: { error: errorMessage },
          bubbles: true,
          composed: true
        }));
        alert(errorMessage);
      }
    } catch (error) {
      this.dispatchEvent(new CustomEvent('pairs-error', {
        detail: { error },
        bubbles: true,
        composed: true
      }));
      alert('There was an error adding items to your cart. Please try again.');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Confirm Selection';
      }
    }
  }

  async manualCartUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      
      // Dispatch cart update event with the new data
      this.dispatchEvent(new CustomEvent('cart-updated', {
        detail: { cartData },
        bubbles: true,
        composed: true
      }));
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, [data-cart-item-count]');
      cartCountElements.forEach(element => {
        element.textContent = cartData.item_count;
      });
      
      // Update cart total elements
      const cartTotalElements = document.querySelectorAll('[data-cart-total]');
      cartTotalElements.forEach(element => {
        const totalInCents = cartData.total_price;
        try {
          if (window.theme?.moneyWithCurrencyFormat && window.themeCurrency?.formatMoney) {
            element.innerHTML = window.themeCurrency.formatMoney(totalInCents, window.theme.moneyWithCurrencyFormat);
          } else if (window.Shopify?.formatMoney) {
            const moneyFormat = window.theme?.moneyFormat || window.theme?.moneyWithCurrencyFormat || "${{amount}}";
            element.innerHTML = window.Shopify.formatMoney(totalInCents, moneyFormat);
          } else {
            element.innerHTML = `$${(totalInCents / 100).toFixed(2)}`;
          }
        } catch (error) {
          console.error('Error formatting money:', error);
          element.innerHTML = `$${(totalInCents / 100).toFixed(2)}`;
        }
        element.setAttribute('data-cart-total', totalInCents);
      });
      
      // Update loyalty points elements
      const loyaltyPointsElements = document.querySelectorAll('[data-lion-points-for]');
      loyaltyPointsElements.forEach(element => {
        element.setAttribute('data-lion-points-for', cartData.total_price);
      });
      
      // Update cart drawer contents
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer) {
        const cartDrawerResponse = await fetch('/cart?section_id=api-cart-items');
        const cartDrawerHTML = await cartDrawerResponse.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(cartDrawerHTML, 'text/html');
        
        // Update cart items
        this.updateCartSection(cartDrawer, doc, '[data-items-holder]');
        
        // Update cart footer
        this.updateCartSection(cartDrawer, doc, '[data-foot-holder]');
        
        // Update free shipping message
        this.updateCartSection(cartDrawer, doc, '.free-shipping');
      }
      
    } catch (error) {
      console.error('Cart update failed:', error);
      this.dispatchEvent(new CustomEvent('cart-update-error', {
        detail: { error },
        bubbles: true,
        composed: true
      }));
      window.location.reload();
    }
  }

  updateCartSection(cartDrawer, doc, selector) {
    const newElement = doc.querySelector(selector);
    const currentElement = cartDrawer.querySelector(selector);
    if (newElement && currentElement) {
      currentElement.innerHTML = newElement.innerHTML;
    }
  }

  disconnectedCallback() {
    // Clean up any event listeners if needed
  }

  static get observedAttributes() {
    return []; // Add observed attributes if needed
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Handle attribute changes if needed
  }
}

// Register the web component
customElements.define('pairs-with-selector', PairsWithSelector);